import { all, call, select, takeEvery, put } from 'redux-saga/effects';

import URLS from 'constants/urls';
import { waitForModal } from 'components/Modal';
import { makePath } from 'helpers/link-generation';
import { fetchEntity } from '@classpass/saga-tools';
import { createInfoBanner, createErrorBanner } from 'components/Banner';
import braze, { BRAZE_CUSTOM_EVENTS } from 'helpers/braze';
import Segment from 'helpers/segment';
import { reportError } from 'helpers/error-logging';
import { resources } from 'store/saga/entities/bound';
import { selectActiveVenueId, selectRouteParams } from 'selectors/general';
import { goToTab, goToItem } from '../ManageServices.saga';
import { transformResourceData } from './Resources.helpers';
import { messages } from './Resources.messages';

export const FETCH_RESOURCES = 'FETCH_RESOURCES';
export const fetchResources = () => ({ type: FETCH_RESOURCES });

export const CREATE_RESOURCE = 'CREATE_RESOURCE';
export const createResource = (payload, formAsyncFns) => ({
  type: CREATE_RESOURCE,
  payload,
  formAsyncFns,
});

export const EDIT_RESOURCE = 'EDIT_RESOURCE';
export const editResource = (data, formAsyncFns) => ({
  type: EDIT_RESOURCE,
  data,
  formAsyncFns,
});

export const UPDATE_SELECTED_RESOURCE = 'UPDATE_SELECTED_RESOURCE';
export const updateSelectedResource = (id, focus) => ({ type: UPDATE_SELECTED_RESOURCE, id, focus });

export const DELETE_RESOURCE = 'DELETE_RESOURCE';
export const deleteResource = id => ({
  type: DELETE_RESOURCE,
  id,
});

export function* doCreateResource(client, action) {
  const { httpClient } = client;
  const { payload, formAsyncFns } = action;
  const { resolveSubmit, rejectSubmit } = formAsyncFns;

  try {
    const { venueId, tab } = yield select(selectRouteParams);
    const endpoint = URLS.manageServices.resources.create(venueId);

    const { data } = yield call([httpClient, httpClient.post], endpoint, transformResourceData(payload));
    const { id: itemId } = data;

    yield all([
      put(fetchResources()),
      put(createInfoBanner(messages.success.resourceCreated)),
      call(resolveSubmit),
      put(goToTab(tab)),
      put(goToItem(itemId)),
    ]);
    yield call(braze.trackEvent, BRAZE_CUSTOM_EVENTS.NEW_EQUIPMENT_ADDED);
    yield call(Segment.event, 'Equipment Created', {
      'equipment id': itemId,
      'venue id': venueId,
      'equipment name': payload.name,
    });
  } catch (error) {
    yield all([put(createErrorBanner(messages.error.problemWithRequest)), call(rejectSubmit)]);
  }
}

export function* doEditResource(client, action) {
  const { data, formAsyncFns } = action;
  const { resolveSubmit, rejectSubmit } = formAsyncFns;

  try {
    const routeParams = yield select(selectRouteParams);
    const { venueId, itemId } = routeParams;
    yield call(fetchEntity, resources, [client, venueId, data]);
    yield all([
      put(fetchResources()),
      put(createInfoBanner(messages.success.resourceEdited)),
      call(resolveSubmit),
      put(goToItem(itemId)),
    ]);
  } catch (error) {
    yield all([put(createErrorBanner(messages.error.problemWithRequest)), call(rejectSubmit)]);
  }
}

export function* doFetchResources(client) {
  try {
    const venueId = yield select(selectActiveVenueId);
    return yield call(fetchEntity, resources, [client, venueId]);
  } catch (error) {
    yield all([put(createErrorBanner(messages.error.problemWithRequest)), call(reportError, error)]);
  }
}

export function* doUpdateSelectedResource(client, action) {
  try {
    const { id, focus } = action;
    const routeParams = yield select(selectRouteParams);
    const newPath = makePath.manageServices({
      ...routeParams,
      itemId: id,
      focus,
    });
    yield call(client.navigate, newPath);
  } catch (error) {
    reportError(error);
  }
}

export function* doDeleteResource(client, action) {
  const { httpClient } = client;
  const { id } = action;

  try {
    const { venueId, tab } = yield select(selectRouteParams);
    const endpoint = URLS.manageServices.resources.delete(venueId, id);
    const confirmed = yield call(waitForModal, client, 'DeleteResourceModal');

    if (confirmed) {
      yield call([httpClient, httpClient.delete], endpoint);
      yield all([put(fetchResources()), put(createInfoBanner(messages.success.resourceDeleted)), put(goToTab(tab))]);
    }
  } catch (error) {
    yield all([put(createErrorBanner(messages.error.problemWithRequest)), call(reportError, error)]);
  }
}

export function* watchActions(client) {
  yield takeEvery(CREATE_RESOURCE, doCreateResource, client);
  yield takeEvery(EDIT_RESOURCE, doEditResource, client);
  yield takeEvery(FETCH_RESOURCES, doFetchResources, client);
  yield takeEvery(UPDATE_SELECTED_RESOURCE, doUpdateSelectedResource, client);
  yield takeEvery(DELETE_RESOURCE, doDeleteResource, client);
}
